import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Alert, Spinner,
    Modal, InputGroup, Badge
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import './Factures.css';

export default function EnvoyerEmailDevis() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [devis, setDevis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    
    const [emailForm, setEmailForm] = useState({
        destinataire: '',
        cc: '',
        bcc: '',
        sujet: '',
        message: '',
        inclurePdf: true
    });

    useEffect(() => {
        fetchDevis();
    }, [id]);

    const fetchDevis = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/devis.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setDevis(result.data);
            
            if (result.data) {
                setEmailForm({
                    ...emailForm,
                    destinataire: result.data.client_email || '',
                    sujet: `Devis ${result.data.numero_proforma} - ${result.data.client_nom}`,
                    message: generateDefaultMessage(result.data)
                });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const generateDefaultMessage = (devis) => {
        const validite = formatDate(devis.date_validite);
        
        return `Bonjour,

Nous avons le plaisir de vous adresser notre devis n° ${devis.numero_proforma} pour les prestations demandées.

Vous trouverez en pièce jointe le détail de notre proposition.

Ce devis est valable jusqu'au ${validite}. Restant à votre disposition pour tout renseignement complémentaire, nous vous prions d'agréer, Madame, Monsieur, l'expression de nos salutations distinguées.

Cordialement,
L'équipe GESTION STOCK`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!emailForm.destinataire) {
            setError("Veuillez saisir une adresse email");
            return;
        }

        setSending(true);
        setError("");

        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;

            const response = await fetch(
                `${API_BASE_URL}/send-email.php`,
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        to: emailForm.destinataire,
                        cc: emailForm.cc,
                        bcc: emailForm.bcc,
                        subject: emailForm.sujet,
                        message: emailForm.message,
                        devis_id: id,
                        include_pdf: emailForm.inclurePdf,
                        id_utilisateur: user?.id || user?.id_utilisateur
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setSuccess("Email envoyé avec succès !");
                setTimeout(() => {
                    navigate(`/ventes/DetailDevis/${id}`);
                }, 2000);
            } else {
                setError(result.message || "Erreur lors de l'envoi de l'email");
            }
        } catch (err) {
            setError("Erreur lors de l'envoi de l'email");
        } finally {
            setSending(false);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: '#64748b', icon: 'ri-draft-line', label: 'Brouillon' },
            envoye: { color: '#3b82f6', icon: 'ri-send-plane-line', label: 'Envoyé' },
            accepte: { color: '#10b981', icon: 'ri-check-double-line', label: 'Accepté' },
            refuse: { color: '#ef4444', icon: 'ri-close-circle-line', label: 'Refusé' },
            converti_facture: { color: '#21ACCD', icon: 'ri-file-list-3-line', label: 'Converti' }
        };
        const config = configs[statut] || configs.brouillon;
        return (
            <span className="status-badge" style={{ 
                background: config.color, 
                color: 'white',
                padding: '0.3rem 0.8rem',
                borderRadius: '20px',
                fontSize: '0.7rem',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem'
            }}>
                <i className={config.icon} style={{ fontSize: '0.8rem' }}></i>
                {config.label}
            </span>
        );
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement du devis...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!devis) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Devis non trouvé</h6>
                                <p className="mb-0 small">Le devis que vous recherchez n'existe pas</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => navigate('/ventes/devisListe')}>
                                    <i className="ri-arrow-left-line me-1"></i>Retour aux devis
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-mail-send-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Envoyer le devis par email</h4>
                                    <p className="text-muted small mb-0">
                                        {devis.numero_proforma} - {devis.client_nom}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate(`/ventes/DetailDevis/${id}`)}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                            <Button 
                                variant="outline-info" 
                                onClick={() => setShowPreviewModal(true)}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-eye-line"></i>
                                Aperçu
                            </Button>
                        </div>
                    </div>

                    {/* Alertes */}
                    {error && (
                        <Alert variant="danger" onClose={() => setError("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert variant="success" onClose={() => setSuccess("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {success}
                        </Alert>
                    )}

                    <Row>
                        {/* Formulaire d'envoi */}
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-mail-send-line me-2"></i>
                                        Composition de l'email
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-3">
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Destinataire <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-user-line" style={{ color: '#21ACCD' }}></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            value={emailForm.destinataire}
                                                            onChange={(e) => setEmailForm({...emailForm, destinataire: e.target.value})}
                                                            placeholder="client@email.com"
                                                            required
                                                            className="border-2"
                                                        />
                                                    </InputGroup>
                                                    <Form.Text className="text-muted small">
                                                        Email du client
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">CC</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-user-line" style={{ color: '#21ACCD' }}></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            value={emailForm.cc}
                                                            onChange={(e) => setEmailForm({...emailForm, cc: e.target.value})}
                                                            placeholder="cc@email.com"
                                                            className="border-2"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">BCC</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-user-line" style={{ color: '#21ACCD' }}></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            value={emailForm.bcc}
                                                            onChange={(e) => setEmailForm({...emailForm, bcc: e.target.value})}
                                                            placeholder="bcc@email.com"
                                                            className="border-2"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Sujet</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-subject-line" style={{ color: '#21ACCD' }}></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            value={emailForm.sujet}
                                                            onChange={(e) => setEmailForm({...emailForm, sujet: e.target.value})}
                                                            required
                                                            className="border-2"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Message</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={8}
                                                        value={emailForm.message}
                                                        onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                                                        className="border-2 rounded-3"
                                                        style={{ fontFamily: 'Arial, sans-serif' }}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Check
                                                        type="checkbox"
                                                        id="include-pdf"
                                                        label="Joindre le devis au format PDF"
                                                        checked={emailForm.inclurePdf}
                                                        onChange={(e) => setEmailForm({...emailForm, inclurePdf: e.target.checked})}
                                                        className="fw-semibold"
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <hr />
                                                <div className="d-flex justify-content-end gap-2">
                                                    <Button 
                                                        variant="outline-secondary" 
                                                        onClick={() => navigate(`/ventes/DetailDevis/${id}`)}
                                                        className="rounded-pill px-4"
                                                    >
                                                        Annuler
                                                    </Button>
                                                    <Button 
                                                        type="submit" 
                                                        variant="warning" 
                                                        disabled={sending}
                                                        className="rounded-pill px-5"
                                                        style={{ background: '#f59e0b', border: 'none' }}
                                                    >
                                                        {sending ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Envoi en cours...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-send-plane-line me-2"></i>
                                                                Envoyer
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Informations du devis */}
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-information-line me-2"></i>
                                        Récapitulatif
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <div className="mb-3">
                                        <small className="text-muted d-block text-uppercase fw-semibold">Devis</small>
                                        <h5 className="mb-0 text-primary">{devis.numero_proforma}</h5>
                                    </div>

                                    <div className="mb-3">
                                        <small className="text-muted d-block text-uppercase fw-semibold">Client</small>
                                        <h6 className="mb-1">{devis.client_nom}</h6>
                                        <p className="text-muted small mb-0">Code: {devis.code_client}</p>
                                    </div>

                                    <div className="mb-3">
                                        <small className="text-muted d-block text-uppercase fw-semibold">Montant total</small>
                                        <h4 className="text-primary mb-0">{formatCurrency(devis.net_a_payer)}</h4>
                                    </div>

                                    <div className="mb-3">
                                        <small className="text-muted d-block text-uppercase fw-semibold">Validité</small>
                                        <p className="mb-0">{formatDate(devis.date_validite)}</p>
                                    </div>

                                    <hr />

                                    <div className="mb-3">
                                        <small className="text-muted d-block text-uppercase fw-semibold">Statut</small>
                                        <div className="mt-2">
                                            {getStatutBadge(devis.statut)}
                                        </div>
                                    </div>

                                    {devis.notes && (
                                        <>
                                            <hr />
                                            <div>
                                                <small className="text-muted d-block text-uppercase fw-semibold">Notes</small>
                                                <p className="small mt-2 mb-0 text-muted">{devis.notes}</p>
                                            </div>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>

                            <Card className="border-0 shadow-sm rounded-3" style={{ background: '#f8fafc' }}>
                                <Card.Body className="p-4">
                                    <h6 className="mb-3 fw-semibold">
                                        <i className="ri-information-line text-primary me-2" style={{ color: '#21ACCD' }}></i>
                                        Informations
                                    </h6>
                                    <ul className="small text-muted ps-3 mb-0" style={{ listStyle: 'none', paddingLeft: 0 }}>
                                        <li className="mb-2 d-flex align-items-center gap-2">
                                            <i className="ri-mail-line text-primary"></i>
                                            L'email sera envoyé avec le devis en pièce jointe
                                        </li>
                                        <li className="mb-2 d-flex align-items-center gap-2">
                                            <i className="ri-user-line text-primary"></i>
                                            Le client recevra une copie du devis
                                        </li>
                                        <li className="mb-2 d-flex align-items-center gap-2">
                                            <i className="ri-refresh-line text-primary"></i>
                                            Le statut du devis sera mis à jour en "Envoyé"
                                        </li>
                                    </ul>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* Modal d'aperçu de l'email */}
            <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg" centered className="email-preview-modal">
                <Modal.Header closeButton className="border-0 pt-4 px-4" style={{ background: 'linear-gradient(135deg, #21ACCD, #1a8ba8)' }}>
                    <Modal.Title className="w-100">
                        <div className="d-flex align-items-center gap-3">
                            <div className="preview-icon">
                                <i className="ri-mail-send-line text-white" style={{ fontSize: '1.8rem' }}></i>
                            </div>
                            <div>
                                <h5 className="mb-0 text-white">Aperçu de l'email</h5>
                                <p className="text-white-50 small mb-0">Vérifiez le contenu avant envoi</p>
                            </div>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body className="p-4">
                    <div className="email-preview-container">
                        {/* En-tête de l'email */}
                        <div className="email-header-card mb-4">
                            <div className="email-header-title">
                                <i className="ri-mail-line me-2"></i>
                                Informations d'envoi
                            </div>
                            <div className="email-header-content">
                                <div className="email-info-row">
                                    <div className="email-info-label">
                                        <i className="ri-send-plane-line"></i>
                                        De :
                                    </div>
                                    <div className="email-info-value">
                                        <span className="fw-semibold">GESTION STOCK</span>
                                        <span className="text-muted mx-1">&lt;contact@gestionstock.ga&gt;</span>
                                    </div>
                                </div>
                                
                                <div className="email-info-row">
                                    <div className="email-info-label">
                                        <i className="ri-user-line"></i>
                                        À :
                                    </div>
                                    <div className="email-info-value">
                                        <span className="fw-semibold text-primary">{emailForm.destinataire || 'Non spécifié'}</span>
                                    </div>
                                </div>
                                
                                {emailForm.cc && (
                                    <div className="email-info-row">
                                        <div className="email-info-label">
                                            <i className="ri-file-copy-line"></i>
                                            CC :
                                        </div>
                                        <div className="email-info-value">
                                            <span className="text-muted">{emailForm.cc}</span>
                                        </div>
                                    </div>
                                )}
                                
                                {emailForm.bcc && (
                                    <div className="email-info-row">
                                        <div className="email-info-label">
                                            <i className="ri-eye-off-line"></i>
                                            BCC :
                                        </div>
                                        <div className="email-info-value">
                                            <span className="text-muted">{emailForm.bcc}</span>
                                        </div>
                                    </div>
                                )}
                                
                                <div className="email-info-row">
                                    <div className="email-info-label">
                                        <i className="ri-quill-pen-line"></i>
                                        Sujet :
                                    </div>
                                    <div className="email-info-value">
                                        <span className="fw-bold">{emailForm.sujet}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Corps du message */}
                        <div className="email-body-card mb-4">
                            <div className="email-body-title">
                                <i className="ri-chat-3-line me-2"></i>
                                Message
                            </div>
                            <div className="email-body-content">
                                {emailForm.message.split('\n').map((paragraph, idx) => (
                                    <p key={idx} className="mb-2">
                                        {paragraph || <br />}
                                    </p>
                                ))}
                            </div>
                        </div>

                        {/* Pièce jointe */}
                        {emailForm.inclurePdf && (
                            <div className="email-attachment-card">
                                <div className="attachment-icon">
                                    <i className="ri-file-pdf-line"></i>
                                </div>
                                <div className="attachment-info">
                                    <div className="attachment-name">Devis_{devis?.numero_proforma}.pdf</div>
                                    <div className="attachment-size">Document PDF • ~150 KB</div>
                                </div>
                                <div className="attachment-badge">
                                    <Badge className="px-3 py-2 rounded-pill" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                                        <i className="ri-attachment-line me-1"></i>
                                        Joint
                                    </Badge>
                                </div>
                            </div>
                        )}

                        {/* Message de confirmation */}
                        <div className="email-footer-note mt-3 text-center">
                            <small className="text-muted">
                                <i className="ri-information-line me-1"></i>
                                Vérifiez les informations avant d'envoyer l'email
                            </small>
                        </div>
                    </div>
                </Modal.Body>
                
                <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center gap-2">
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowPreviewModal(false)}
                        className="rounded-pill px-4"
                    >
                        <i className="ri-close-line me-2"></i>
                        Fermer
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={() => {
                            setShowPreviewModal(false);
                            handleSubmit(new Event('submit'));
                        }}
                        className="rounded-pill px-4"
                        style={{ background: '#21ACCD', border: 'none' }}
                    >
                        <i className="ri-mail-send-line me-2"></i>
                        Envoyer l'email
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
/* Email Preview Modal */
.email-preview-modal .modal-content {
    border-radius: 24px;
    border: none;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
}

.preview-icon {
    width: 48px;
    height: 48px;
    background: rgba(255,255,255,0.2);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.email-preview-container {
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 5px;
}

.email-preview-container::-webkit-scrollbar {
    width: 6px;
}

.email-preview-container::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 10px;
}

.email-preview-container::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
}

/* Email Header Card */
.email-header-card {
    background: linear-gradient(135deg, #f8fafc, #ffffff);
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
}

.email-header-title {
    background: #f1f5f9;
    padding: 12px 16px;
    font-weight: 600;
    color: #1e293b;
    border-bottom: 1px solid #e2e8f0;
}

.email-header-content {
    padding: 16px;
}

.email-info-row {
    display: flex;
    margin-bottom: 12px;
    flex-wrap: wrap;
}

.email-info-row:last-child {
    margin-bottom: 0;
}

.email-info-label {
    width: 70px;
    color: #64748b;
    font-size: 0.85rem;
    display: flex;
    align-items: center;
    gap: 6px;
}

.email-info-label i {
    font-size: 1rem;
    color: #94a3b8;
}

.email-info-value {
    flex: 1;
    font-size: 0.9rem;
    color: #1e293b;
    word-break: break-word;
}

/* Email Body Card */
.email-body-card {
    background: #ffffff;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
}

.email-body-title {
    background: #f1f5f9;
    padding: 12px 16px;
    font-weight: 600;
    color: #1e293b;
    border-bottom: 1px solid #e2e8f0;
}

.email-body-content {
    padding: 16px;
    background: #fafcff;
    font-size: 0.9rem;
    line-height: 1.6;
    color: #334155;
    max-height: 250px;
    overflow-y: auto;
}

/* Email Attachment Card */
.email-attachment-card {
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: all 0.2s ease;
}

.email-attachment-card:hover {
    background: #f1f5f9;
    border-color: #21ACCD;
}

.attachment-icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #fee2e2, #fecaca);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.attachment-icon i {
    font-size: 1.5rem;
    color: #dc2626;
}

.attachment-info {
    flex: 1;
}

.attachment-name {
    font-weight: 600;
    color: #1e293b;
    font-size: 0.9rem;
}

.attachment-size {
    font-size: 0.7rem;
    color: #94a3b8;
}

.attachment-badge {
    flex-shrink: 0;
}

/* Email Footer Note */
.email-footer-note {
    padding: 12px;
    background: #f8fafc;
    border-radius: 12px;
}

/* Responsive */
@media (max-width: 768px) {
    .email-info-row {
        flex-direction: column;
        gap: 4px;
    }
    
    .email-info-label {
        width: 100%;
    }
    
    .email-attachment-card {
        flex-direction: column;
        text-align: center;
    }
    
    .preview-icon {
        width: 40px;
        height: 40px;
    }
    
    .preview-icon i {
        font-size: 1.3rem;
    }
}

.border-2 {
    border-width: 2px !important;
    border-color: #e2e8f0;
}

.border-2:focus {
    border-color: #21ACCD;
    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
}

.rounded-3 {
    border-radius: 12px !important;
}

.alert-modern {
    border: none;
    border-radius: 12px;
    padding: 1rem 1.5rem;
    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
}

 `}</style>
        </>
    );
}